import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PeopleIcon from '@mui/icons-material/People';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Paper,
    Skeleton,
    Snackbar,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';

const Dashboard = ({ user, token }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [syncMsg, setSyncMsg] = useState('');
    const canSync = ['owner', 'admin', 'manager'].includes(user?.role);

    useEffect(() => {
        if (!token) {
            setLoading(false);
            return undefined;
        }

        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 12000);

        const load = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_URL}/api/dashboard/stats`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    signal: controller.signal
                });
                const json = await res.json();
                if (json.success) setData(json.data);
            } catch (e) {
                if (e.name !== 'AbortError') {
                    console.error('Dashboard error:', e);
                }
            } finally {
                window.clearTimeout(timeoutId);
                setLoading(false);
            }
        };

        load();
        return () => {
            window.clearTimeout(timeoutId);
            controller.abort();
        };
    }, [token]);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/dashboard/stats`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const json = await res.json();
            if (json.success) setData(json.data);
        } catch (e) {
            console.error('Dashboard error:', e);
        } finally {
            setLoading(false);
        }
    };

    const handleSync = async () => {
        if (!canSync) {
            setSyncMsg('❌ Недостаточно прав для синхронизации');
            return;
        }

        setSyncing(true);
        setSyncMsg('');
        try {
            const now = new Date();
            const dateFrom = new Date(now.getFullYear(), now.getMonth(), 1)
                .toISOString().split('T')[0];
            const dateTo = new Date(now.getFullYear(), now.getMonth() + 1, 0)
                .toISOString().split('T')[0];
            const authHeaders = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            };

            const eventsResponse = await fetch(`${API_URL}/api/yandex/sync-events`, {
                method: 'POST',
                headers: authHeaders
            });
            const eventsData = await eventsResponse.json();

            const ordersResponse = await fetch(`${API_URL}/api/yandex/sync-orders-to-visits`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ date_from: dateFrom, date_to: dateTo })
            });
            const ordersData = await ordersResponse.json();

            const ticketsResponse = await fetch(`${API_URL}/api/yandex/sync-tickets`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ date_from: dateFrom, date_to: dateTo })
            });
            const ticketsData = await ticketsResponse.json();

            if (!ordersData.success && !ticketsData.success) {
                setSyncMsg(`❌ Ошибка: ${ordersData.message || ticketsData.error || 'синхронизация не выполнена'}`);
            } else {
                const syncedTickets = ticketsData.synced ?? ((ticketsData.inserted || 0) + (ticketsData.updated || 0));
                setSyncMsg(
                    `✅ ${now.toLocaleString('ru-RU', { month: 'long' })}: события ${(eventsData.inserted || 0) + (eventsData.updated || 0)}, `
                    + `заказы ${ordersData.inserted || 0}, билеты ${syncedTickets}`
                );
            }
            await fetchStats();
        } catch (e) {
            setSyncMsg('❌ Ошибка соединения');
        } finally {
            setSyncing(false);
        }
    };

    const formatNumber = (n) => {
        if (n === undefined || n === null) return '—';
        return Number(n).toLocaleString('ru-RU');
    };

    const formatMoney = (n) => {
        if (n === undefined || n === null) return '—';
        return Number(n).toLocaleString('ru-RU') + ' ₽';
    };

    const formatSyncTime = (isoStr) => {
        if (!isoStr) return 'нет данных';
        const d = new Date(isoStr);
        return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    };

    const now = new Date();
    const monthName = now.toLocaleString('ru-RU', { month: 'long', year: 'numeric' });

    const cards = data ? [
        {
            title: 'Билетов продано',
            subtitle: monthName,
            value: formatNumber(data.tickets?.value),
            icon: <ConfirmationNumberIcon sx={{ fontSize: 40 }} />,
            color: '#f59e0b',
            bgColor: '#fef3c7'
        },
        {
            title: 'Выручка',
            subtitle: monthName,
            value: formatMoney(data.revenue?.value),
            icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
            color: '#10b981',
            bgColor: '#d1fae5'
        },
        {
            title: 'Сотрудников',
            subtitle: 'активных',
            value: formatNumber(data.employees?.value),
            icon: <PeopleIcon sx={{ fontSize: 40 }} />,
            color: '#0ea5e9',
            bgColor: '#dbeafe'
        },
    ] : [];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#1e293b', mb: 0 }}>
                    Панель управления 👋
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!loading && data?.last_yandex_sync && (
                        <Typography variant="caption" color="text.secondary">
                            Синхр.: {formatSyncTime(data.last_yandex_sync)}
                        </Typography>
                    )}
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />}
                        onClick={handleSync}
                        disabled={syncing || !canSync}
                        sx={{ minWidth: 160 }}
                        title={canSync ? 'Синхронизация заказов из Яндекса' : 'Недостаточно прав для синхронизации'}
                    >
                        {syncing ? 'Синхронизация...' : 'Синхр. с Яндексом'}
                    </Button>
                    <Button size="small" variant="text" onClick={fetchStats} disabled={loading}>
                        <RefreshIcon fontSize="small" />
                    </Button>
                </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Данные за {monthName} · Обновлено: {now.toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
            </Typography>

            <Snackbar open={!!syncMsg} autoHideDuration={5000} onClose={() => setSyncMsg('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity={syncMsg.startsWith('✅') ? 'success' : 'error'} onClose={() => setSyncMsg('')}>
                    {syncMsg}
                </Alert>
            </Snackbar>

            <Grid container spacing={3}>
                {loading
                    ? [0, 1, 2].map(i => (
                        <Grid item xs={12} sm={6} md={3} key={i}>
                            <Card sx={{ height: 140 }}>
                                <CardContent>
                                    <Skeleton variant="text" width="60%" />
                                    <Skeleton variant="text" width="40%" height={48} />
                                    <Skeleton variant="text" width="50%" />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                    : cards.map((card, i) => (
                        <Grid item xs={12} sm={6} md={3} key={i}>
                            <Card sx={{
                                height: '100%',
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                transition: 'all 0.3s ease',
                                '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 12px 40px rgba(0,0,0,0.15)' },
                            }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box>
                                            <Typography color="text.secondary" variant="body2" sx={{ fontWeight: 500 }}>
                                                {card.title}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {card.subtitle}
                                            </Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 700, color: card.color, my: 0.5 }}>
                                                {card.value}
                                            </Typography>
                                        </Box>
                                        <Avatar sx={{ bgcolor: card.bgColor, color: card.color, width: 56, height: 56 }}>
                                            {card.icon}
                                        </Avatar>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                }
            </Grid>

            <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} md={8}>
                    <Paper sx={{
                        p: 3,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)',
                    }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                            🎉 Музей на ощупь — панель управления
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
                            Используйте меню слева для навигации по разделам системы.
                        </Typography>
                        {loading && (
                            <Box sx={{ mt: 2 }}>
                                <CircularProgress size={24} sx={{ color: 'white' }} />
                            </Box>
                        )}
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{
                        p: 3,
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        color: 'white',
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(240, 147, 251, 0.4)',
                        height: '100%',
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>📊 За {monthName}</Typography>
                        {loading ? (
                            <>
                                <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                                <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                                <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                            </>
                        ) : data && (
                            <Box>
                                <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Билетов продано</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{formatNumber(data.tickets?.value)}</Typography>
                                </Box>
                                <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Выручка</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{formatMoney(data.revenue?.value)}</Typography>
                                </Box>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Dashboard;
